import { createRef, Component, Fragment } from "react";
import * as serviceWorkerRegistration from "../../serviceWorkerRegistration";
import { CSSTransition } from "react-transition-group";
import styled from "styled-components/macro";
import { rem } from "polished";

import { BREAKPOINT_WRAPPER } from "../../constants";
import history from "../../helpers/history";
import { colors } from "../../styles/Styles.variables";

import Button from "../../styles/components/Button";

const updateTimeOut = 500;

const withUpdates = WrappedComponent => {
  const Updates = class extends Component {
    constructor(props) {
      super(props);

      this.checkForUpdates = this.checkForUpdates.bind(this);
      this.handleEnter = this.handleEnter.bind(this);
      this.handleSuccess = this.handleSuccess.bind(this);
      this.handleUpdate = this.handleUpdate.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.loadUpdates = this.loadUpdates.bind(this);
      this.watchVisibility = this.watchVisibility.bind(this);

      this._registration = null;
      this.button = createRef();
      this.nodeRef = createRef();

      this.state = {
        hasUpdate: false,
        hidden: null,
        visibilityChange: null,
      };
    }

    checkForUpdates() {
      if (!!this._registration) {
        this._registration.update();
      }
    }

    handleEnter() {
      if (!!this.button.current) {
        this.button.current.focus();
      }
    }

    handleSuccess(registration) {
      if (!!registration) {
        this._registration = registration;
        this.unlisten = history.listen(this.checkForUpdates);
        this.watchVisibility();
      }
    }

    handleUpdate(registration) {
      if (!!registration) {
        this._registration = registration;
        this.setState({
          hasUpdate: true,
        });
      }
    }

    handleVisibilityChange() {
      const { hidden } = this.state;
      if (!!hidden && !document[hidden]) {
        this.checkForUpdates();
      }
    }

    loadUpdates() {
      if (!!this._registration) {
        this._registration.unregister().then(() => {
          if (
            !!history.location &&
            !!history.location.state &&
            !!history.location.state.notFound
          ) {
            window.location.href = history.location.state.notFound;
          } else {
            window.location.reload(true);
          }
        });
      } else {
        window.location.reload(true);
      }
    }

    watchVisibility() {
      let hidden, visibilityChange;

      if (typeof document.addEventListener !== "undefined") {
        if (typeof document.hidden !== "undefined") {
          hidden = "hidden";
          visibilityChange = "visibilitychange";
        } else if (typeof document.msHidden !== "undefined") {
          hidden = "msHidden";
          visibilityChange = "msvisibilitychange";
        } else if (typeof document.webkitHidden !== "undefined") {
          hidden = "webkitHidden";
          visibilityChange = "webkitvisibilitychange";
        }
      }

      this.setState(
        {
          hidden: hidden,
          visibilityChange: visibilityChange,
        },
        () => {
          if (!!hidden && !!visibilityChange) {
            document.addEventListener(
              visibilityChange,
              this.handleVisibilityChange,
              false
            );
          }
        }
      );
    }

    componentDidMount() {
      serviceWorkerRegistration.register({
        onSuccess: this.handleSuccess,
        onUpdate: this.handleUpdate,
      });
    }

    componentWillUnmount() {
      const { visibilityChange } = this.state;

      if (!!this.unlisten) {
        this.unlisten();
      }

      if (!!visibilityChange) {
        document.removeEventListener(
          visibilityChange,
          this.handleVisibilityChange
        );
      }
    }

    render() {
      const { button, handleEnter, loadUpdates, nodeRef } = this;
      const { hasUpdate } = this.state;

      return (
        <Fragment>
          <CSSTransition
            appear
            classNames="update"
            in={hasUpdate}
            mountOnEnter
            nodeRef={nodeRef}
            onEnter={handleEnter}
            timeout={updateTimeOut}
          >
            <Notification ref={nodeRef}>
              <p>Updates Available!</p>
              <Button
                color="green500"
                onClick={loadUpdates}
                ref={button}
                size="small"
                type="button"
              >
                Load Updates
              </Button>
            </Notification>
          </CSSTransition>
          <WrappedComponent {...this.props} updateAvailable={hasUpdate} />
        </Fragment>
      );
    }
  };

  return Updates;
};

const Notification = styled.div`
  align-items: center;
  background-color: ${colors.green400};
  border-radius: 0 0 ${rem("8px")} ${rem("8px")};
  display: flex;
  height: ${rem("90px")};
  left: 50%;
  padding: ${rem("24px")} ${rem("24px")} ${rem("18px")};
  position: fixed;
  top: ${rem("-180px")};
  transform: translateX(-50%);
  white-space: nowrap;
  z-index: 9999999999999999999;

  p {
    color: ${colors.white};
    display: none;
    font-size: ${rem("18px")};
    line-height: 1;
    margin: 0 ${rem("24px")} 0 0;

    @media ${BREAKPOINT_WRAPPER} {
      display: block;
    }
  }

  &.update-appear,
  &.update-enter {
    top: ${rem("-180px")};
  }

  &.update-appear-active,
  &.update-enter-active {
    top: ${rem("-6px")};
    transition: top ${updateTimeOut}ms;
  }

  &.update-appear-done,
  &.update-enter-done {
    top: ${rem("-6px")};
  }
`;

export default withUpdates;
